import { useState, useRef, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft,  } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from 'react-i18next';
import { useLocale } from '../context/LocaleContext';
import { useCart } from "../context/CartContext";
import { useCookieConsent } from "../context/CookieConsentContext";
import { useProducts } from "../context/ProductsContext";
import { useMedia } from "../context/MediaContext";
import { Product, ProductVariant } from '../types/product-variant';
import { orderProductMedia } from '../utils/media';
import { getShopState } from '../context/shopState';
import cartAddSound from '../assets/sounds/Add2Cart.mp3';
import SEO from '../components/SEO';
import ProductImages from '../components/product/ProductImages';
import MobileProductGallery, { MobileAdditionalMedia } from '../components/product/MobileProductGallery';
import ProductInfo from '../components/product/ProductInfo';
import Lightbox from '../components/product/Lightbox';
import RelatedProducts, { getRelatedProducts } from '../components/product/RelatedProducts';
import { Helmet } from 'react-helmet-async';
import { trackAddToCart, trackViewContent, generateEventId, getMetaCookies } from '../components/MetaPixel';
import { getUtmForCapi } from '../utils/utm';
import EnhancedProductSchema from '../components/EnhancedProductSchema';
import { useStock } from '../context/StockContext';
import ProductReviews from '../components/product/ProductReviews';
import reviewsData from '../data/reviews.json';
import { ProductReview } from '../types/product-variant';
const ProductPage = () => {
    const { t } = useTranslation();
    const { localePath } = useLocale();
    const { slug } = useParams<{ slug: string }>();
    const { products, loading: productsLoading } = useProducts();
    const { getMediaByProduct, getGenericPackaging, loading: mediaLoading } = useMedia();
    const product = products?.find(p => p.slug === slug) ?? null;
    const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [isAdded, setIsAdded] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const { items, addItem, updateQuantity } = useCart();
    const { consent } = useCookieConsent();
    const [showVariantError, setShowVariantError] = useState(false);
    const [ringSize, setRingSize] = useState('');
    const [showRingSizeError, setShowRingSizeError] = useState(false);
    const [audio] = useState(new Audio(cartAddSound));
    const { getStock, isMadeToOrder } = useStock();
    const [stockError, setStockError] = useState<string | null>(null);

    // Adjustable rings don't need size selection
    const ADJUSTABLE_RING_SLUGS = ['pez-fontana-r', 'lapislazuli-pez'];
    const isRing = product?.category === 'Rings';
    const isAdjustableRing = isRing && ADJUSTABLE_RING_SLUGS.includes(product?.slug || '');
    
    audio.volume = 0.2;

    // Ring sizes use size-specific cart IDs so different sizes are separate cart items
    const ringCartId = (isRing && ringSize && product?.id) ? `${product.id}__${ringSize.replace(' ', '')}` : null;
    const cartItemId = ringCartId ?? selectedVariant?.id ?? product?.id;
    const cartItem = items.find(item => item.id === cartItemId);

    const [localQuantity, setLocalQuantity] = useState(cartItem?.quantity || 1);
    const removalTimeout = useRef<NodeJS.Timeout>();

    // Sync localQuantity with cart when cart item changes
    useEffect(() => {
        if (cartItem) {
            setLocalQuantity(cartItem.quantity);
        }
    }, [cartItem?.id, cartItem?.quantity]);

    // Sync isAdded with whether current variant/product is in cart
    useEffect(() => {
        setIsAdded(!!cartItem);
    }, [cartItem]);

    const resolveProductMedia = () => {
        // Don't query media until both products and media are loaded
        if (!product || productsLoading || mediaLoading) return [];
        
        let media = product.airtableId ? getMediaByProduct(product.airtableId) : [];
        if (media.length === 0 && product.id) {
            media = getMediaByProduct(product.id);
        }
        if (media.length === 0 && product.slug) {
            media = getMediaByProduct(product.slug);
        }
        return media;
    };

    const productMedia = resolveProductMedia();
    const ordered = orderProductMedia(productMedia);
    // Variant images override media when a variant is selected
    const variantImages: string[] = selectedVariant?.images
        ? (Array.isArray(selectedVariant.images)
            ? selectedVariant.images
            : [selectedVariant.images.main, selectedVariant.images.model, selectedVariant.images.zoom].filter(Boolean) as string[])
        : [];

    // Unified media list — order comes from usage tags in DB, no image/video splitting
    type MediaItem = { type: 'image' | 'video'; src: string };
    const allMedia: MediaItem[] = (variantImages.length > 0
        ? variantImages.map(src => ({ type: 'image' as const, src }))
        : ordered.map(m => ({ type: (m.mediaType ?? 'image') as 'image' | 'video', src: m.cloudinaryURL! }))
    ).filter((m, i, arr) => m.src && arr.findIndex(x => x.src === m.src) === i);

    const packaging = getGenericPackaging();

    // For SEO, cart image, structured data — pick image URLs only (skip videos)
    const imageUrls = allMedia.filter(m => m.type === 'image').map(m => m.src);
    const activeImages = {
        main: imageUrls[0] ?? '',
        secondary: imageUrls[1] ?? packaging,
        third: imageUrls[2] ?? packaging,
    };

    if (import.meta.env.DEV && product) {
        console.debug('[media] product page', {
            slug: product.slug,
            mediaCount: productMedia.length,
            allMedia: allMedia.length,
            records: productMedia.map(m => ({ id: m.id, usage: m.usage, url: m.cloudinaryURL?.slice(-40) })),
        });
    }

    // Carousel: everything after hero, in usage-tag order
    let sortedCarouselMedia = allMedia.slice(1)
        .filter((item, i, arr) => item.src && arr.findIndex(x => x.src === item.src) === i);

    // Pad with packaging if odd count (keeps 2-column carousel grid even)
    if (sortedCarouselMedia.length > 0 && sortedCarouselMedia.length % 2 === 1) {
        sortedCarouselMedia.push({ type: 'image' as const, src: packaging });
    }

    const lightboxImages = [
        activeImages.main,
        ...sortedCarouselMedia.map(m => m.src)
    ].filter(Boolean);

    const handleAddToCart = () => {
        if (!product) return;

        // Check stock and made-to-order status from Airtable via context
        const variantId = selectedVariant?.id;
        const stockToCheck = variantId || product.id;
        const availableStock = getStock(stockToCheck);
        const madeToOrder = isMadeToOrder(stockToCheck);
        const isInStock = availableStock > 0;
        const canPurchase = isInStock || madeToOrder;

        if (!canPurchase) {
            return; // Don't allow adding out-of-stock items (unless made-to-order)
        }

        if (product.variants && product.variants.length > 0 && !selectedVariant) {
            setShowVariantError(true);
            setTimeout(() => {
                setShowVariantError(false);
            }, 3000);
            return;
        }

        // Require ring size for non-adjustable rings
        if (isRing && !isAdjustableRing && !ringSize) {
            setShowRingSizeError(true);
            setTimeout(() => setShowRingSizeError(false), 3000);
            return;
        }

        // Build cart item name with variant material and ring size
        let itemName = product.name;
        if (selectedVariant) itemName += ` - ${selectedVariant.material}`;
        if (isRing && ringSize) itemName += ` (${ringSize})`;
        if (isAdjustableRing) itemName += ` (${t('ringSize.adjustable')})`;

        // Ring sizes get size-specific IDs so each size is a separate cart item
        const cartId = (isRing && ringSize) ? `${product.id}__${ringSize.replace(' ', '')}` : selectedVariant?.id ?? product.id;

        const itemToAdd = {
            id: cartId,
            name: itemName,
            price: selectedVariant?.price ?? product?.price ?? product?.basePrice ?? 0,
            quantity: 1,
            image: activeImages.main,
            oneOfKind: product.oneOfKind,
            madeToOrder: madeToOrder,
            slug: product.slug,
            variantId: selectedVariant?.id ?? null
        };

        if (navigator.vibrate) {
            navigator.vibrate(100);
        }

        try {
            audio.currentTime = 0;
            audio.play().catch(err => console.log('Audio play failed:', err));
        } catch (err) {
            console.log('Audio playback error:', err);
        }

        addItem(itemToAdd);
        setIsAdded(true);
        setShowSuccess(true);
        
        setTimeout(() => {
            setShowSuccess(false);
        }, 2000);

        const atcEventId = generateEventId();
        const atcValue = selectedVariant?.price ?? product.price ?? 0;
        trackAddToCart({
            content_name: product.name,
            content_ids: [product.id],
            value: atcValue,
            currency: 'EUR'
        }, consent.marketing, atcEventId);

        // Send server-side CAPI AddToCart event
        const atcCookies = getMetaCookies();
        fetch('/api/notify?action=meta-event', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                eventName: 'AddToCart',
                eventId: atcEventId,
                userData: { fbp: atcCookies.fbp, fbc: atcCookies.fbc },
                customData: {
                    content_name: product.name,
                    content_ids: [product.id],
                    value: atcValue,
                    currency: 'EUR',
                    ...getUtmForCapi(),
                },
            }),
        }).catch(() => {});
    };

    const MAX_QUANTITY_PER_ITEM = 5;

    const handleQuantityChange = (newQuantity: number) => {
        if (!product) return;

        const availableStock = getStock(product.id, selectedVariant?.id);
        const madeToOrder = isMadeToOrder(product.id, selectedVariant?.id);
        const isMadeToOrderItem = availableStock === 0 && madeToOrder;

        // Enforce max quantity limit for all items
        if (newQuantity > MAX_QUANTITY_PER_ITEM) {
            setStockError(`Maximum ${MAX_QUANTITY_PER_ITEM} per item. Contact us for larger orders.`);
            setTimeout(() => setStockError(null), 4000);
            return;
        }

        // For made-to-order items, only enforce max limit (not stock limit)
        if (!isMadeToOrderItem && newQuantity > availableStock) {
            setStockError(`Only ${availableStock} items available in stock`);
            setTimeout(() => setStockError(null), 3000);
            return;
        }

        const validQuantity = Math.max(0, newQuantity);
        setLocalQuantity(validQuantity);

        // Use size-specific ID for ring items to match the cart entry
        const itemId = (isRing && ringSize) ? `${product.id}__${ringSize.replace(' ', '')}` : selectedVariant?.id ?? product.id;
        
        if (validQuantity === 0) {
            if (removalTimeout.current) {
                clearTimeout(removalTimeout.current);
            }
            removalTimeout.current = setTimeout(() => {
                updateQuantity(itemId, 0);
                setIsAdded(false);
            }, 800);
        } else {
            if (removalTimeout.current) {
                clearTimeout(removalTimeout.current);
            }
            updateQuantity(itemId, validQuantity);
        }
    };

    const handleVariantChange = (variantId: string) => {
        const variant = product?.variants?.find(v => v.id === variantId);
        setSelectedVariant(variant || null);
    };

    const getBackToShopUrl = () => {
        const state = getShopState();
        const params = new URLSearchParams();
        
        if (state.filters.sortBy !== 'newest') {
            params.append('sort', state.filters.sortBy);
        }
        if (state.filters.category !== 'all') {
            params.append('category', state.filters.category);
        }
        if (state.filters.collection !== 'all') {
            params.append('collection', state.filters.collection);
        }
        if (state.filters.showOneOfKind !== null) {
            params.append('oneOfKind', state.filters.showOneOfKind.toString());
        }
        if (state.currentPage > 1) {
            params.append('page', state.currentPage.toString());
        }

        const queryString = params.toString();
        return `${localePath('/shop')}${queryString ? `?${queryString}` : ''}`;
    };

    // Use resolved activeImages (already absolute Cloudinary URLs)
    const ensureAbsolute = (url: string) =>
        url.startsWith('http') ? url : `https://www.camilaparente.store${url}`;
    const productImageUrl = activeImages.main ? ensureAbsolute(activeImages.main) : '';
    const modelImageUrl = activeImages.secondary ? ensureAbsolute(activeImages.secondary) : '';
    const zoomImageUrl = activeImages.third ? ensureAbsolute(activeImages.third) : '';

    // Create array of all product images with full URLs
    const allProductImages = [productImageUrl, modelImageUrl, zoomImageUrl].filter(Boolean);

    const productSchema = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product?.name,
        description: product?.description,
        image: allProductImages, // Array of all product image URLs
        url: `https://www.camilaparente.store/product/${product?.slug}`,
        sku: product?.id,
        brand: {
            "@type": "Brand",
            name: "Camila Parente"
        },
        offers: {
            "@type": "Offer",
            url: `https://www.camilaparente.store/product/${product?.slug}`,
            priceCurrency: "EUR",
            price: product?.price,
            itemCondition: "https://schema.org/NewCondition",
            availability: "https://schema.org/InStock"
        }
    };

    useEffect(() => {
        setSelectedVariant(null);
        setSelectedImage(null);
        setIsAdded(false);
        setShowSuccess(false);
        setLocalQuantity(1);
    }, [product?.id]);

    // Track ViewContent when product page loads
    useEffect(() => {
        if (!product || productsLoading) return;
        const eventId = generateEventId();
        trackViewContent({
            content_name: product.name,
            content_ids: [product.id],
            content_type: 'product',
            value: product.price ?? 0,
            currency: 'EUR',
        }, consent.marketing, eventId);

        // Send server-side CAPI event
        const { fbp, fbc } = getMetaCookies();
        fetch('/api/notify?action=meta-event', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                eventName: 'ViewContent',
                eventId,
                userData: { fbp, fbc },
                customData: {
                    content_name: product.name,
                    content_ids: [product.id],
                    content_type: 'product',
                    value: product.price ?? 0,
                    currency: 'EUR',
                    ...getUtmForCapi(),
                },
            }),
        }).catch(() => {}); // fire-and-forget
    }, [product?.id, productsLoading]);

    if (productsLoading) {
        return <div className="text-center py-20">{t('product.loading')}</div>;
    }

    if (!product) {
        return <div className="text-center py-20">{t('product.notFound')}</div>;
    }

    return (
        <>
            <SEO
                title={product?.name || 'Product'}
                description={product?.['SEO-description'] || product?.description || ''}
                image={productImageUrl}
                type="product"
            />
            <Helmet>
                {/* Product-specific meta tags (not covered by SEO component) */}
                <meta property="product:price:amount" content={String(selectedVariant?.price ?? product?.price)} />
                <meta property="product:price:currency" content="EUR" />
                <meta property="product:availability" content={product?.inStock ? "in stock" : "out of stock"} />
            </Helmet>

            {product && (
                <EnhancedProductSchema
                    product={product}
                    currentUrl={window.location.href}
                    images={allProductImages}
                />
            )}
            {/* Mobile: gallery breaks out of Layout for full-bleed */}
            <div className="md:hidden">
                {/* Full-bleed gallery — negative margin escapes Layout's px-2 */}
                <div className="-mx-2">
                    <MobileProductGallery
                        product={product}
                        selectedVariant={selectedVariant}
                        onImageClick={setSelectedImage}
                    />
                </div>

                <div className="px-2 pt-5">
                    <ProductInfo
                        product={product}
                        selectedVariant={selectedVariant}
                        isAdded={isAdded}
                        localQuantity={localQuantity}
                        onAddToCart={handleAddToCart}
                        onQuantityChange={handleQuantityChange}
                        showVariantError={showVariantError}
                        onVariantChange={handleVariantChange}
                        stockError={stockError}
                        imageUrl={activeImages.main}
                        ringSize={ringSize}
                        onRingSizeChange={setRingSize}
                        showRingSizeError={showRingSizeError}
                    />
                </div>

                {/* Full-bleed additional media */}
                <div className="-mx-2">
                    <MobileAdditionalMedia
                        product={product}
                        selectedVariant={selectedVariant}
                        onImageClick={setSelectedImage}
                    />
                </div>
            </div>

            {/* Desktop: original padded layout */}
            <div className="hidden md:block container mx-auto px-4 py-8 max-w-[min(80vw,1400px)] mt-2">
                <Link
                    to={getBackToShopUrl()}
                    className="inline-flex items-center gap-2 mb-6 text-black dark:text-white hover:text-brand-blue dark:hover:text-brand-blue transition-colors"
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
                    <span>{t('product.backToShop')}</span>
                </Link>

                <div className="grid grid-cols-2 gap-8">
                    <ProductImages
                        product={product}
                        selectedVariant={selectedVariant}
                        onImageClick={setSelectedImage}
                    />

                    <ProductInfo
                        product={product}
                        selectedVariant={selectedVariant}
                        isAdded={isAdded}
                        localQuantity={localQuantity}
                        onAddToCart={handleAddToCart}
                        onQuantityChange={handleQuantityChange}
                        showVariantError={showVariantError}
                        onVariantChange={handleVariantChange}
                        stockError={stockError}
                        imageUrl={activeImages.main}
                        ringSize={ringSize}
                        onRingSizeChange={setRingSize}
                        showRingSizeError={showRingSizeError}
                    />
                </div>
            </div>

            {/* Shared components */}
            <div className="md:container md:mx-auto md:px-4 md:max-w-[min(80vw,1400px)]">
                {stockError && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
                    >
                        {stockError}
                    </motion.div>
                )}

                <Lightbox
                    selectedImage={selectedImage}
                    images={lightboxImages}
                    onClose={() => setSelectedImage(null)}
                />

                <AnimatePresence>
                    {showSuccess && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 50 }}
                            className="fixed bottom-4 right-4 bg-brand-blue text-white px-4 py-2 rounded-lg shadow-lg"
                        >
                            {t('product.itemAdded')}
                        </motion.div>
                    )}
                </AnimatePresence>

                {product && (
                    <>
                        <ProductReviews
                            productId={product.id}
                            reviews={reviewsData.reviews.map((r): ProductReview => ({
                                ...r,
                                media: r.media?.map(m => ({
                                    ...m,
                                    type: m.type as "image" | "video"
                                })) || []
                            }))}
                            isOneOfKind={product.oneOfKind}
                            collection={product.collection}
                        />
                        <div className="relative">
                            <RelatedProducts
                                products={getRelatedProducts(product, products || [], items)}
                            />
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default ProductPage;